/**
 * Query Helper Utilities
 *
 * Common patterns for Supabase queries in V2 hooks.
 */

import type { PostgrestError } from '@supabase/supabase-js';

/** Stale time constants for TanStack Query hooks. */
export const STALE_TIME_5M = 5 * 60 * 1000;
export const STALE_TIME_10M = 10 * 60 * 1000;
export const STALE_TIME_30M = 30 * 60 * 1000;

/** Garbage collection time constants for TanStack Query hooks. */
export const GC_TIME_10M = 10 * 60 * 1000;
export const GC_TIME_1H = 60 * 60 * 1000;

/** Defaults for search-type queries (full-text search, semantic search). */
export const searchQueryDefaults = {
  staleTime: STALE_TIME_5M,
  gcTime: GC_TIME_10M,
  refetchOnWindowFocus: false,
  retry: 1,
} as const;

/** Defaults for standard V2 queries (most list and detail fetches). */
export const standardQueryDefaults = {
  staleTime: STALE_TIME_5M,
  gcTime: GC_TIME_10M,
  refetchOnWindowFocus: false,
} as const;

/** Defaults for slowly-changing reference data (taxonomy, filter options, status options). */
export const referenceDataDefaults = {
  staleTime: STALE_TIME_30M,
  gcTime: GC_TIME_1H,
  refetchOnWindowFocus: false,
} as const;

/**
 * PGRST116 error code indicates no rows returned for .single() query.
 * This is expected behavior when a record doesn't exist.
 */
const PGRST116_NO_ROWS = 'PGRST116';

/**
 * PostgreSQL unique constraint violation error code.
 * Thrown when inserting a duplicate value into a unique column.
 */
export const POSTGRES_UNIQUE_VIOLATION = '23505';

/**
 * Result type for Supabase queries that return a single row.
 */
export interface SingleQueryResult<T> {
  data: T | null;
  error: PostgrestError | null;
}

/**
 * Handle single-row fetch with PGRST116 error handling.
 *
 * Returns null when no row is found (PGRST116), throws for other errors.
 * Use this for .single() queries where "not found" is a valid result.
 *
 * @param result - The result from a Supabase .single() query
 * @param entityName - Entity name for error messages (e.g., "bill", "committee")
 * @returns The data if found, null if not found
 * @throws Error for database errors other than PGRST116
 *
 * @example
 * const { data, error } = await supabase
 *   .from('bills_v2')
 *   .select('*')
 *   .eq('id', billId)
 *   .single();
 *
 * return handleSingleResult({ data, error }, 'bill');
 */
export function handleSingleResult<T>(result: SingleQueryResult<T>, entityName: string): T | null {
  const { data, error } = result;

  if (error) {
    if (error.code === PGRST116_NO_ROWS) {
      return null;
    }
    throw new Error(`Failed to fetch ${entityName}: ${error.message}`);
  }

  return data;
}

/**
 * Execute a single-row query and handle PGRST116 errors.
 *
 * This is an async wrapper that awaits the query and handles errors.
 * Named to parallel handleSingleResult (sync) with handleSingleResultAsync.
 *
 * @param queryPromise - Promise from a Supabase .single() query
 * @param entityName - Entity name for error messages
 * @returns The data if found, null if not found
 * @throws Error for database errors other than PGRST116
 *
 * @example
 * return handleSingleResultAsync(
 *   supabase.from('bills_v2').select('*').eq('id', billId).single(),
 *   'bill'
 * );
 */
export async function handleSingleResultAsync<T>(
  queryPromise: PromiseLike<SingleQueryResult<T>>,
  entityName: string
): Promise<T | null> {
  const result = await queryPromise;
  return handleSingleResult(result, entityName);
}

/**
 * Check if an error should be ignored (PGRST116 for optional queries).
 *
 * Use this when you want to continue execution even if no row is found.
 *
 * @param error - PostgrestError or null
 * @returns true if the error is PGRST116 (no rows), false otherwise
 */
export function isPGRST116(error: PostgrestError | null): boolean {
  return error?.code === PGRST116_NO_ROWS;
}

/**
 * Dev-only assertion that the first row of a query result matches the expected shape.
 *
 * Logs a console.warn if the type guard fails on `data[0]`. Skipped entirely in production.
 * Uses `process.env.NODE_ENV` for compatibility with both Vite and Jest environments.
 *
 * @param data - Array of rows from a Supabase query
 * @param guard - Type guard function to validate the shape
 * @param label - Label for the warning message (typically the function name)
 */
export function assertDevShape<T>(
  data: unknown[],
  guard: (row: unknown) => row is T,
  label: string
): void {
  if (process.env.NODE_ENV === 'production') return;
  if (data.length > 0 && !guard(data[0])) {
    console.warn(`[${label}] First row failed type guard. Got:`, data[0]);
  }
}
